import { jwtDecode } from 'jwt-decode';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { getSessionStorage, setSessionStorage } from '../utils/helpers';
import { getEnvConfig } from '../utils/getEnv';

const env = getEnvConfig();

class AuthService {
  constructor() {
    this.isRedirecting = false;
    this.tokenRefreshInterval = null;
    this.apiBaseUrl = env.AC_BASE_URL;
    this.isLocalHost = window.location.hostname === 'localhost';
  }

  async setUserDetails(idToken) {
    const res = await axios
      .get(`${env.PYTHON_BASE_URL + 'sda/GetLoggedUserDetails'}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          ApiKey: `${env.MICROSERVICE_API_KEY}`
        }
      })
      .then((res) => {
        setSessionStorage('EMAIL', res.data.data.emailId);
        setSessionStorage('USER_ID', res.data.data.userId);
        setSessionStorage('PERMISSION_LEVEL', res.data.data.permissionLevel);
        setSessionStorage('USER_TYPE', res.data.data.userType);
        setSessionStorage('ENCRYPTED_EMAIL', res.data.data.encryptedEmailId);
        setSessionStorage('IS_ASSESSOR', res.data.data.isAssessor);
        setSessionStorage('IS_SME', res.data.data.isSME);
      })
      .catch(() => {
        return null;
      });
  }

  getUserToken() {
    const idToken = this.decrypt(getSessionStorage('SI_IT'));
    if (!idToken) {
      return null;
    }
    return idToken;
  }

  async isAuthenticated() {
    const idToken = this.decrypt(sessionStorage.getItem('SI_IT'));
    if (!idToken) {
      return false;
    }
    try {
      const decodedToken = jwtDecode(idToken);
      const expiresAt = parseInt(getSessionStorage('SI_EXP'), 10);
      //return new Date().getTime() < expiresAt && decodedToken.exp * 1000 > new Date().getTime();
      if (!(new Date().getTime() < expiresAt && decodedToken.exp * 1000 > new Date().getTime())) {
        await this.refreshToken();
        console.log('Token refreshed');
        return true;
      }
    } catch (e) {
      console.error('Invalid token', e);
      return false;
    }
  }

  async signinRedirect(currentPath) {
    if (!this.isRedirecting) {
      this.isRedirecting = true;
      this.clearSession();
      setSessionStorage('SI_RD_P', this.encryt(currentPath));

      const authUrl = `${env.OIDC_AUTHORITY}/authorize?response_type=${
        env.OIDC_RESPONSE_TYPE
      }&client_id=${env.OIDC_CLIENT_ID}&redirect_uri=${encodeURIComponent(
        env.OIDC_REDIRECT_URI
      )}&scope=${encodeURIComponent(env.OIDC_SCOPE)}`;
      window.location.href = authUrl;
    }
  }

  async exchangeToken(authCode) {
    const urlParams = new URLSearchParams(window.location.search);
    const authorizationCode = urlParams.get('code');
    if (!authorizationCode) {
      throw new Error('Authorization code not found in the URL');
    }

    const tokenUrl = this.isLocalHost ? `/sdaapi/token` : `${env.OIDC_AUTHORITY}/token`;

    const body = new URLSearchParams();
    body.append('grant_type', 'authorization_code');
    body.append('code', authorizationCode);
    body.append('redirect_uri', env.OIDC_REDIRECT_URI);
    body.append('client_id', env.OIDC_CLIENT_ID);
    body.append('client_secret', env.OIDC_CLIENT_SECRET);

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed with status ${response.status}`);
    }

    const data = await response.json();
    this.setSession(data);
    await this.setUserDetails(data.id_token);
    window.history.replaceState({}, document.title, '/');
    this.isRedirecting = false;
  }

  async clearSession() {
    sessionStorage.removeItem('SI_U');
    sessionStorage.removeItem('SI_AT');
    sessionStorage.removeItem('SI_RT');
    sessionStorage.removeItem('SI_IT');
    sessionStorage.removeItem('SI_EXP');
    sessionStorage.removeItem('EMAIL');
    sessionStorage.removeItem('USER_ID');
    sessionStorage.removeItem('PERMISSION_LEVEL');
    clearInterval(this.tokenRefreshInterval);
    sessionStorage.removeItem('USER_TYPE');
  }

  setSession(authResult) {
    const expiresAt = JSON.stringify(authResult.expires_in * 1000 + new Date().getTime());
    setSessionStorage('SI_AT', this.encryt(authResult.access_token));
    setSessionStorage('SI_RT', this.encryt(authResult.refresh_token));
    setSessionStorage('SI_EXP', expiresAt);
    setSessionStorage('SI_IT', this.encryt(authResult.id_token));
    this.scheduleTokenRefresh();
  }

  async handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');

    if (!authCode) {
      throw new Error('No authorization code found in the callback URL');
    }

    await this.exchangeToken(authCode);
  }

  encryt(text) {
    return text;
    return text ? CryptoJS.AES.encrypt(text, env.OIDC_ENCRYPT_KEY).toString() : null;
  }

  decrypt(encryptedText) {
    return encryptedText;
    if (!encryptedText) return null;
    const bytes = CryptoJS.AES.decrypt(encryptedText, env.OIDC_ENCRYPT_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  decodeToken(token) {
    if (!token) {
      throw new Error('Token is undefined or empty');
    }

    const base64Url = token.split('.')[1];
    if (!base64Url) {
      throw new Error('Token is not properly formatted');
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    try {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (e) {
      throw new Error('Error decoding token: ' + e.message);
    }
  }

  scheduleTokenRefresh() {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }
    const expiresAt = JSON.parse(sessionStorage.getItem('SI_EXP'), 10);
    const refreshIn = expiresAt - new Date().getTime() - 60000; // 1 minute before expiry
    if (refreshIn > 0) {
      this.tokenRefreshInterval = setInterval(async () => {
        try {
          await this.refreshToken();
        } catch (error) {
          throw new Error('Error while refreshing the token: ' + e.message);
        }
      }, refreshIn);
    }
  }

  async refreshToken() {
    const refreshToken = this.decrypt(sessionStorage.getItem('SI_RT'));
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const tokenUrl = this.isLocalHost ? `/sdaapi/token` : `${env.OIDC_AUTHORITY}/token`;
    const body = new URLSearchParams();
    body.append('grant_type', 'refresh_token');
    body.append('refresh_token', refreshToken);
    body.append('client_id', env.OIDC_CLIENT_ID);
    body.append('client_secret', env.OIDC_CLIENT_SECRET);

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed with status ${response.status}`);
    }

    const data = await response.json();
    this.setSession(data);
    //this.storeTokens(data.access_token, data.id_token, data.refresh_token);
  }
}

export default new AuthService();